// theme-system/LayoutEngine.jsx
// This is the "Master Builder" of the app. 
// It reads the JSON schema from SchemaContext and decides which Header, Footer, and Page components to actually put on the screen.
// It handles the high-level layout structure (Header -> Main Content -> Footer).

import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getComponent } from './ComponentRegistry';
import { useSchema } from './SchemaContext';
import {
  LAYOUT_MAIN_NO_FOOTER_CLASS,
  useHideFooterContent,
  useHideBottomNav,
} from './FooterExclusions';

/** Stable fallback so `useMemo(..., [themeSettings])` does not invalidate every render when the key is missing. */
const EMPTY_THEME_SETTINGS = Object.freeze({});

/** Fills a slot when `themeSettings` omits that slot or it has no renderable config (e.g. only header, no footer). */
const DEFAULT_RIOCITY_CHROME = Object.freeze({
  header: {
    components: [{ type: 'Header', skin: 'riocity' }],
  },
  footer: {
    components: [{ type: 'Footer', skin: 'riocity' }],
  },
});

/** Fires once when this subtree has committed (must sit inside the Suspense that wraps lazy chrome). */
const ChromeMountSignal = ({ onMounted, children }) => {
  useEffect(() => {
    onMounted();
  }, [onMounted]);
  return <>{children}</>;
};

/**
 * Render a single component or a list of components
 * @param {() => void} [onLayoutChromeReady] - If set (layout header/footer only), one Suspense gates the whole slot;
 *   children stay unloaded until this fires (lazy chunks resolved).
 */
export const RenderComponents = ({ name, data, onLayoutChromeReady }) => {
  if (!data) return null;
  
  // Handle both single component object and array of components
  const components = Array.isArray(data) 
    ? data 
    : (data.components || (data.type ? [data] : null));

  if (!components?.length) return null;
  
  const suspenseFallback = <div className="block-loading" />;

  const mapItem = (config, index) => {
    const Component = getComponent(config.type, config.skin || null);
    if (!Component) return null;
    const key = `${name}-${config.type}-${index}`;

    if (onLayoutChromeReady && (config.type === "Header" || config.type === "Footer")) {
      return <Component key={key} skin={config.skin} />;
    }

    if (config.type === "Header" || config.type === "Footer") {
      return (
        <Suspense key={key} fallback={suspenseFallback}>
          <Component skin={config.skin} />
        </Suspense>
      );
    }

    return (
      <Suspense key={key} fallback={suspenseFallback}>
        <Component {...(config.props || {})} />
      </Suspense>
    );
  };

  const result = components.map(mapItem);

  if (onLayoutChromeReady) {
    return (
      <Suspense fallback={suspenseFallback}>
        <ChromeMountSignal onMounted={onLayoutChromeReady}>
          <>{result}</>
        </ChromeMountSignal>
      </Suspense>
    );
  }

  return result;
};

const LayoutEngine = ({ children }) => {
  const schema = useSchema();
  const hideFooterContent = useHideFooterContent();
  const hideBottomNav = useHideBottomNav();
  // v2 schema structures
  const themeSettings = schema?.themeSettings ?? EMPTY_THEME_SETTINGS;

  // Derive all layout metadata in one pass
  const layout = useMemo(() => {
    const hasContent = (r) => {
      if (!r) return false;
      if (Array.isArray(r)) return r.length > 0;
      if (r.components && Array.isArray(r.components)) return r.components.length > 0;
      if (r.type) return true; // Single component object
      return false;
    };

    const headerData = hasContent(themeSettings.header)
      ? themeSettings.header
      : DEFAULT_RIOCITY_CHROME.header;
    const footerData = hasContent(themeSettings.footer)
      ? themeSettings.footer
      : DEFAULT_RIOCITY_CHROME.footer;

    const showHeader = hasContent(headerData);
    const showFooter = hasContent(footerData);

    return {
      showHeader, showFooter, headerData, footerData
    };
  }, [themeSettings]);

  const [headerReady, setHeaderReady] = useState(!layout.showHeader);
  const [footerReady, setFooterReady] = useState(!layout.showFooter);
  const onHeaderMounted = useCallback(() => setHeaderReady(true), []);
  const onFooterMounted = useCallback(() => setFooterReady(true), []);

  // Only reset readiness when header/footer slots actually *change* after mount.
  // A mount-only effect that always set false ran *after* ChromeMountSignal effects and wiped
  // setHeaderReady/setFooterReady(true) when lazies were already resolved (e.g. cached chunks).
  const prevSlotsRef = useRef(null);
  useEffect(() => {
    const h = layout.showHeader;
    const f = layout.showFooter;
    if (prevSlotsRef.current === null) {
      prevSlotsRef.current = { h, f };
      return;
    }
    const prev = prevSlotsRef.current;
    if (prev.h !== h) {
      setHeaderReady(!h);
    }
    if (prev.f !== f) {
      setFooterReady(!f);
    }
    prevSlotsRef.current = { h, f };
  }, [layout.showHeader, layout.showFooter]);

  const chromeReady = headerReady && footerReady;

  return (
    <div className="layout">
      {/* Global Header */}
      {layout.showHeader && (
        <div className="layout__header">
          <RenderComponents name="header" data={layout.headerData} onLayoutChromeReady={onHeaderMounted} />
        </div>
      )}

      <main className="layout__middle">
        <section
          className={
            hideFooterContent || hideBottomNav
              ? `layout__main ${LAYOUT_MAIN_NO_FOOTER_CLASS} ${hideBottomNav ? "hide-bottom-nav" : ""}`
              : 'layout__main'
          }
        >
          {chromeReady ? children : <div className="block-loading" />}
        </section>
      </main>

      {/* Global Footer */}
      {layout.showFooter && (
        <div className="layout__footer">
          <RenderComponents name="footer" data={layout.footerData} onLayoutChromeReady={onFooterMounted} />
        </div>
      )}
    </div>
  );
};

const LayoutEngineWrapper = (props) => (
  <>
    <LayoutEngine {...props} />
  </>
);

export default LayoutEngineWrapper;
